const MIB = 1024 * 1024
const MAX_BLOCKS = 50_000
const UPLOAD_CONCURRENCY = 3
const MAX_ATTEMPTS = 4
const STORAGE_VERSION = '2023-11-03'

export const waitForTransfer = (milliseconds: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Cancelled', 'AbortError'))
    const abort = () => {
      clearTimeout(timer)
      reject(new DOMException('Cancelled', 'AbortError'))
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }, milliseconds)
    signal?.addEventListener('abort', abort, { once: true })
  })

class StorageRequestError extends Error {
  constructor(public readonly status: number) {
    super('The file could not be uploaded. Check your connection and try again.')
  }
}

const putBlob = (
  url: string,
  body: Blob | string,
  headers: Record<string, string>,
  signal: AbortSignal,
  onProgress?: (bytes: number) => void
) =>
  new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    const abort = () => request.abort()
    const cleanup = () => signal.removeEventListener('abort', abort)
    request.open('PUT', url)
    request.timeout = 5 * 60 * 1000
    request.setRequestHeader('x-ms-version', STORAGE_VERSION)
    for (const [name, value] of Object.entries(headers)) request.setRequestHeader(name, value)
    request.upload.onprogress = (event) => onProgress?.(event.loaded)
    request.onload = () => {
      cleanup()
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new StorageRequestError(request.status))
    }
    request.onerror = () => {
      cleanup()
      reject(new StorageRequestError(0))
    }
    request.ontimeout = () => {
      cleanup()
      reject(new StorageRequestError(408))
    }
    request.onabort = () => {
      cleanup()
      reject(new DOMException('Cancelled', 'AbortError'))
    }
    signal.addEventListener('abort', abort, { once: true })
    if (signal.aborted) {
      cleanup()
      reject(new DOMException('Cancelled', 'AbortError'))
    } else request.send(body)
  })

export const uploadBlobInBlocks = async (
  file: File,
  initialUrl: string,
  renewUrl: () => Promise<string>,
  onProgress: (percentage: number) => void,
  signal: AbortSignal
) => {
  const blockSize = Math.max(8 * MIB, Math.ceil(file.size / MAX_BLOCKS / MIB) * MIB)
  const blockCount = Math.ceil(file.size / blockSize)
  const blockIds = Array.from({ length: blockCount }, (_, index) =>
    btoa(index.toString().padStart(8, '0'))
  )
  const transferred = new Array<number>(blockCount).fill(0)
  let transferredTotal = 0
  let reportedProgress = -1
  const controller = new AbortController()
  const abort = () => controller.abort()
  signal.addEventListener('abort', abort, { once: true })
  if (signal.aborted) controller.abort()
  let uploadUrl = initialUrl
  let renewal: Promise<string> | null = null
  let nextBlock = 0

  const request = async (
    parameters: Record<string, string>,
    body: Blob | string,
    headers: Record<string, string>,
    progress?: (bytes: number) => void
  ) => {
    for (let attempt = 0; ; attempt++) {
      const url = new URL(uploadUrl)
      for (const [name, value] of Object.entries(parameters)) url.searchParams.set(name, value)
      try {
        await putBlob(url.toString(), body, headers, controller.signal, progress)
        return
      } catch (error) {
        if (!(error instanceof StorageRequestError) || attempt >= MAX_ATTEMPTS - 1) throw error
        if (error.status === 403) {
          renewal ??= renewUrl().finally(() => {
            renewal = null
          })
          uploadUrl = await renewal
        } else if (![0, 408, 429].includes(error.status) && error.status < 500) throw error
        await waitForTransfer(500 * 2 ** attempt, controller.signal)
      }
    }
  }

  try {
    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, blockCount) }, async () => {
        while (nextBlock < blockCount) {
          const index = nextBlock++
          const body = file.slice(index * blockSize, Math.min(file.size, (index + 1) * blockSize))
          await request(
            { comp: 'block', blockid: blockIds[index] },
            body,
            { 'Content-Type': 'application/octet-stream' },
            (bytes) => {
              const count = Math.min(bytes, body.size)
              transferredTotal += count - transferred[index]
              transferred[index] = count
              const percentage = Math.min(99, Math.floor((transferredTotal / file.size) * 100))
              if (percentage > reportedProgress) {
                reportedProgress = percentage
                onProgress(percentage)
              }
            }
          )
        }
      })
    )
    const manifest = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`
    await request({ comp: 'blocklist' }, manifest, {
      'Content-Type': 'application/xml',
      'x-ms-blob-content-type': 'application/zip'
    })
    onProgress(100)
  } catch (error) {
    controller.abort()
    throw error
  } finally {
    signal.removeEventListener('abort', abort)
  }
}

export const downloadSignedFile = (url: string) => {
  if (!['https:', 'http:'].includes(new URL(url).protocol))
    throw new Error('The download link is invalid.')
  const link = document.createElement('a')
  link.href = url
  link.referrerPolicy = 'no-referrer'
  link.download = ''
  document.body.appendChild(link)
  link.click()
  link.remove()
}
